import React, { useEffect, useMemo, useState } from "react";
import { usePlanningStore } from "@store/planningStore";
import { useAtelierStore } from "@store/atelierStore";
import { useDisponibiliteStore } from "@store/disponibiliteStore";
import { useDataStore } from "@store/user_org";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import SearchAlgolia from "@components/ui/search/SearchAlgolia";
import MiniCalendar from "@components/ui/MiniCalendar";
import { AvailabilityChart } from "./AvailabilityChart";
import { computeWeeklyAvailability } from "./computeAvailability";
import { CategoryFilters } from "./CategoryFilters";
import { TimeGridWithAppointments } from "./TimeGridWithAppointments";
import { WeekTimeGridReception } from "./WeekTimeGridReception";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { searchOrdresFull } from "@services/algolia";
import { ordresApi } from "@services/api/ordres";
import OrdreModal from "@components/modals/OrdreModal";
import type { Ordre } from "types/ordre";

function tsToDateStr(ts?: { seconds: number }): string | null {
  if (!ts) return null;
  const d = new Date(ts.seconds * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function ReceptionScreen() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchAppointments = usePlanningStore((s) => s.fetchAppointments);
  const fetchAtelierData = useAtelierStore((s) => s.fetchData);
  const fetchBlocks = useDisponibiliteStore((s) => s.fetchBlocks);

  useEffect(() => {
    if (orgData?.id) {
      fetchAppointments(orgData.id);
      fetchAtelierData(orgData.id);
      fetchBlocks(orgData.id);
    }
  }, [orgData?.id]);

  const selectedDate = usePlanningStore((s) => s.selectedDate);
  const setSelectedDate = usePlanningStore((s) => s.setSelectedDate);
  const viewMode = usePlanningStore((s) => s.viewMode);
  const setViewMode = usePlanningStore((s) => s.setViewMode);
  const [algoliaQuery, setAlgoliaQuery] = useState("");
  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);

  const workers = useAtelierStore((s) => s.workers);
  const jobs = useAtelierStore((s) => s.jobs);
  const blocks = useDisponibiliteStore((s) => s.blocks);
  const workHours = orgData?.infos?.work_hours;

  const weeklyAvailability = useMemo(
    () => computeWeeklyAvailability(selectedDate, workers, jobs, blocks, workHours),
    [selectedDate, workers, jobs, blocks, workHours],
  );

  return (
    <div
      className="flex flex-col overflow-auto h-screen"
      style={{ background: "#FAFAFA" }}
    >
      <div className="flex flex-col h-fit">
        {/* Title */}
        <div
          className="flex flex-col px-4 lg:px-10 pt-8"
        >
          <Text1>Planning réception</Text1>
          <Text2>Planifiez vos rendez-vous clients ici.</Text2>
        </div>

        <div
          className="flex flex-row flex-1 px-4 lg:px-10 pt-5"
          style={{
            gap: 30,
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {/* Left column */}
          <div
            className="flex flex-col"
            style={{
              width: 196,
              flexShrink: 0,
              gap: 20,
              overflowY: "auto",
              paddingBottom: 30,
            }}
          >
            <MiniCalendar
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              weekHighlight={viewMode === "semaine"}
              showNavButtons
            />
            <div className="flex flex-col" style={{ gap: 15 }}>
              <span
                style={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: 12,
                  color: "#A1A1A1",
                }}
              >
                Dispo. atelier(s)
              </span>
              {weeklyAvailability.map((week, i) => (
                <AvailabilityChart key={i} week={week} />
              ))}
            </div>
          </div>

          {/* Right column */}
          <div
            className="flex flex-col flex-1"
            style={{
              gap: 10,
              minWidth: 0,
              overflowY: "auto",
              paddingBottom: 30,
            }}
          >
            <div className="flex flex-row items-center" style={{ gap: 10 }}>
              <div style={{ width: 250, flexShrink: 0 }}>
                <SearchAlgolia
                  value={algoliaQuery}
                  setValue={setAlgoliaQuery}
                  placeholder="Rechercher un ordre..."
                  classNameCustom="w-full"
                  onSearch={async (query) => {
                    if (!orgData?.id) return [];
                    return searchOrdresFull(query, orgData.id);
                  }}
                  onAction={(option) => {
                    if (typeof option === "object" && option.value) {
                      ordresApi.get(option.value).then((ordre) => {
                        const date = tsToDateStr(ordre.reception_date) || tsToDateStr(ordre.created_at);
                        if (date) setSelectedDate(date);
                        setSelectedOrdre(ordre);
                      });
                      return true;
                    }
                  }}
                />
              </div>
              <ChunkyTabs
                activeTab={viewMode}
                tabs={[
                  { value: "jour", label: "Jour" },
                  { value: "semaine", label: "Semaine" },
                ]}
                setActiveTab={(tab) => setViewMode(tab as "jour" | "semaine")}
              />
            </div>
            <CategoryFilters />
            {viewMode === "jour" ? (
              <TimeGridWithAppointments />
            ) : (
              <WeekTimeGridReception />
            )}
          </div>
        </div>
      </div>
      {selectedOrdre && (
        <OrdreModal
          ordre={selectedOrdre}
          onClose={() => setSelectedOrdre(null)}
          onSave={() => setSelectedOrdre(null)}
        />
      )}
    </div>
  );
}
